import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState, useRef } from 'react';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Upload, Package, X } from 'lucide-react';

const BACKEND = RUNTIME_BACKEND_URL;

const TYPES = [
  { v: 'panoramik', l: 'Panoramik Album' },
  { v: 'klasik', l: 'Klasik Album' },
  { v: 'digital', l: 'Dijital Album' },
  { v: 'canvas', l: 'Kanvas / Tablo' },
  { v: 'other', l: 'Diğer' },
];

export default function AlbumcuProductsPage() {
  const { token } = useAuthStore();
  const [products, setProducts] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', price: 0, album_type: 'panoramik', sample_images: [], variants: [], active: true });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    const res = await fetch(`${BACKEND}/api/albumcu/products/me`, { headers });
    setProducts(await res.json());
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', description: '', price: 0, album_type: 'panoramik', sample_images: [], variants: [], active: true });
    setShowDialog(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '', description: p.description || '', price: p.price || 0,
      album_type: p.album_type || 'panoramik', sample_images: p.sample_images || [],
      variants: p.variants || [], active: p.active !== false
    });
    setShowDialog(true);
  };

  const addVariant = () => {
    setForm(prev => ({ ...prev, variants: [...(prev.variants || []), { id: `v_${Date.now()}`, name: '', price: 0, description: '' }] }));
  };
  const updateVariant = (idx, key, value) => {
    setForm(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => i === idx ? { ...v, [key]: value } : v)
    }));
  };
  const removeVariant = (idx) => {
    setForm(prev => ({ ...prev, variants: prev.variants.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Ürün adı gerekli');
    try {
      const url = editing ? `${BACKEND}/api/albumcu/products/me/${editing.id}` : `${BACKEND}/api/albumcu/products/me`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error();
      toast.success(editing ? 'Güncellendi' : 'Eklendi');
      setShowDialog(false);
      load();
    } catch { toast.error('Kaydedilemedi'); }
  };

  const del = async (p) => {
    if (!window.confirm(`${p.name} ürününü silmek istiyor musunuz?`)) return;
    try {
      await fetch(`${BACKEND}/api/albumcu/products/me/${p.id}`, { method: 'DELETE', headers });
      toast.success('Silindi');
      load();
    } catch { toast.error('Silinemedi'); }
  };

  const uploadSample = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !editing) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch(`${BACKEND}/api/albumcu/products/me/${editing.id}/sample`, {
        method: 'POST', headers, body: fd
      });
      const data = await res.json();
      setForm(prev => ({ ...prev, sample_images: [...(prev.sample_images || []), data.filename] }));
      toast.success('Görsel yüklendi');
    } catch { toast.error('Yüklenemedi'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const removeSample = (fn) => {
    setForm(prev => ({ ...prev, sample_images: prev.sample_images.filter(x => x !== fn) }));
  };

  return (
    <div className="space-y-4" data-testid="albumcu-products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Ürünlerim</h1>
          <p className="text-sm text-slate-500">Fotoğrafçılara göstereceğiniz albüm ürünleri ve fiyatları</p>
        </div>
        <Button onClick={openNew} data-testid="add-product-btn">
          <Plus className="h-4 w-4 mr-1" />Yeni Ürün
        </Button>
      </div>

      {products.length === 0 ? (
        <Card><CardContent className="p-10 text-center">
          <Package className="h-12 w-12 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">Henüz ürününüz yok</p>
        </CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {products.map(p => {
            const variants = p.variants || [];
            const hasVariants = variants.length > 0;
            const minExtra = hasVariants ? Math.min(...variants.map(v => v.price || 0)) : 0;
            const maxExtra = hasVariants ? Math.max(...variants.map(v => v.price || 0)) : 0;
            const minPrice = (p.price || 0) + minExtra;
            const maxPrice = (p.price || 0) + maxExtra;
            return (
            <Card key={p.id} data-testid={`product-${p.id}`}>
              <CardContent className="p-3">
                {p.sample_images?.[0] ? (
                  <img src={`${BACKEND}/api/albumcu-samples/${p.sample_images[0]}`}
                    alt="" className="w-full h-32 object-cover rounded mb-2" />
                ) : (
                  <div className="w-full h-32 bg-slate-100 rounded mb-2 flex items-center justify-center">
                    <Package className="h-10 w-10 text-slate-300" />
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{p.name}</div>
                    <div className="text-xs text-slate-500">{TYPES.find(t => t.v === p.album_type)?.l || p.album_type}</div>
                    {hasVariants ? (
                      <div>
                        <div className="text-[10px] text-slate-500">
                          {variants.length} seçenek
                        </div>
                        <div className="text-sm font-bold text-emerald-600">
                          {minPrice.toLocaleString('tr-TR')}
                          {minPrice !== maxPrice && ` - ${maxPrice.toLocaleString('tr-TR')}`} TL
                        </div>
                      </div>
                    ) : (
                      <div className="text-lg font-bold text-emerald-600">{p.price?.toLocaleString('tr-TR')} TL</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(p)}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => del(p)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                {!p.active && <div className="text-[10px] text-amber-600 mt-1">Pasif (fotoğrafçılar göremez)</div>}
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Ürünü Düzenle' : 'Yeni Ürün'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Ürün Adı *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Örn: 30x30 Panoramik Album" data-testid="product-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tip</Label>
                <select value={form.album_type} onChange={e => setForm({ ...form, album_type: e.target.value })}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  {TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              <div>
                <Label>Fiyat (TL)</Label>
                <Input type="number" value={form.price} onChange={e => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                  data-testid="product-price-input" />
              </div>
            </div>
            <div>
              <Label>Açıklama</Label>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                rows={2} placeholder="Premium deri kapak, 20 sayfa..." />
            </div>
            <div>
              <Label>Örnek Görseller</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {(form.sample_images || []).map((fn, i) => (
                  <div key={i} className="relative">
                    <img src={`${BACKEND}/api/albumcu-samples/${fn}`} alt=""
                      className="w-16 h-16 object-cover rounded border" />
                    <button onClick={() => removeSample(fn)}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {editing && (
                  <label className="w-16 h-16 border-2 border-dashed rounded flex items-center justify-center cursor-pointer hover:border-primary">
                    <input type="file" accept="image/*" ref={fileRef} onChange={uploadSample} className="hidden" />
                    {uploading ? '...' : <Upload className="h-4 w-4 text-slate-400" />}
                  </label>
                )}
              </div>
              {!editing && <p className="text-xs text-slate-400 mt-1">Önce ürünü kaydedin, sonra görsel ekleyebilirsiniz</p>}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Ek Seçenekler / Boyutlar (Varyantlar)</Label>
                <Button size="sm" variant="outline" onClick={addVariant} type="button" data-testid="add-variant-btn">
                  <Plus className="h-3 w-3 mr-1" />Seçenek Ekle
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mb-2">
                Örn: "Aile Seti +1500 TL", "35x50 Boyut +500 TL". Boş bırakırsanız sadece ana fiyat gösterilir. Fotoğrafçılar "X TL'den başlayan" olarak görür.
              </p>
              {(form.variants || []).length > 0 && (
                <div className="space-y-2">
                  {form.variants.map((v, i) => (
                    <div key={v.id || i} className="bg-slate-50 border rounded p-2 space-y-1">
                      <div className="grid grid-cols-12 gap-2">
                        <Input className="col-span-6 h-8 text-xs" placeholder="Seçenek adı (örn. 35x50 cm, Aile Seti)"
                          value={v.name} onChange={e => updateVariant(i, 'name', e.target.value)} />
                        <div className="col-span-4 relative">
                          <Input className="h-8 text-xs pr-10" type="number" placeholder="Ek ücret"
                            value={v.price} onChange={e => updateVariant(i, 'price', parseFloat(e.target.value) || 0)} />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">TL</span>
                        </div>
                        <Button size="sm" variant="ghost" className="col-span-2 h-8 text-red-500" onClick={() => removeVariant(i)}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <Input className="h-7 text-[11px]" placeholder="Açıklama (opsiyonel)"
                        value={v.description} onChange={e => updateVariant(i, 'description', e.target.value)} />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="active" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />
              <Label htmlFor="active" className="cursor-pointer">Aktif (fotoğrafçılar görebilir)</Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDialog(false)}>İptal</Button>
              <Button onClick={save} data-testid="save-product-btn">Kaydet</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
